import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

// Customer chat inbox (admin-api /support-chat, changeset 0068). Polling pauses
// while the tab is hidden (react-query's default for refetchInterval).
const INBOX_POLL_MS = 15_000;
const BADGE_POLL_MS = 60_000;

export type ConversationStatus = 'open' | 'resolved';

export interface ConversationSummary {
  id: number;
  userId: number;
  status: ConversationStatus;
  lastMessageAt: string;
  lastSender: 'user' | 'staff' | null;
  unread: boolean;
  /** Has the member read the chat since the team's latest reply; null with no team reply (older admin-api: absent). */
  memberSeenReply?: boolean | null;
  lastMessage: string | null;
  memberName: string | null;
  memberEmail: string | null;
}

export interface ConversationListResponse {
  conversations: ConversationSummary[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface ConversationFilters {
  status: ConversationStatus | 'all';
  unread?: boolean;
  q?: string;
}

export interface ChatMessage {
  id: number;
  /** tippa: an automatic Ask Tippa answer. */
  sender: 'user' | 'staff' | 'tippa';
  /** A member's Ask Tippa tap or Tippa's answer, rather than a typed message. */
  askTippa?: boolean;
  body: string;
  pagePath: string | null;
  createdAt: string;
  staffName?: string | null;
  staffEmail?: string | null;
}

export interface ConversationDetail {
  conversation: {
    id: number;
    userId: number;
    status: ConversationStatus;
    lastMessageAt: string;
    /** When the member last read the chat: opening it with replies waiting, or writing. */
    userLastReadAt: string | null;
    createdAt: string;
    subscriptionLevel: string | null;
    memberSince: string;
    chatEnabled: boolean;
    memberName: string | null;
    memberEmail: string | null;
  };
  messages: ChatMessage[];
}

export function useConversations(page: number, filters: ConversationFilters) {
  return useQuery({
    queryKey: ['support-chat', 'list', page, filters],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), status: filters.status });
      if (filters.unread) params.set('unread', '1');
      if (filters.q) params.set('q', filters.q);
      return apiClient<ConversationListResponse>(`/support-chat/conversations?${params.toString()}`);
    },
    refetchInterval: INBOX_POLL_MS,
  });
}

export function useConversation(id: number | null) {
  return useQuery({
    queryKey: ['support-chat', 'conversation', id],
    queryFn: () => apiClient<ConversationDetail>(`/support-chat/conversations/${id}`),
    enabled: id != null,
    refetchInterval: INBOX_POLL_MS,
  });
}

export function useUnreadConversationCount() {
  return useQuery({
    queryKey: ['support-chat', 'unread-count'],
    queryFn: () => apiClient<{ count: number }>('/support-chat/unread-count'),
    refetchInterval: BADGE_POLL_MS,
    // A role without the section (none today) or a blip should never break the sidebar.
    retry: false,
  });
}

export function useUserConversation(userId: number) {
  return useQuery({
    queryKey: ['support-chat', 'user', userId],
    queryFn: () => apiClient<{ conversationId: number | null }>(`/support-chat/users/${userId}`),
  });
}

function useInvalidateChat() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['support-chat'] });
}

export function useSendReply() {
  const invalidate = useInvalidateChat();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) =>
      apiClient<ChatMessage>(`/support-chat/conversations/${id}/messages`, { method: 'POST', body: JSON.stringify({ body }) }),
    onSuccess: invalidate,
  });
}

export function useSetConversationStatus() {
  const invalidate = useInvalidateChat();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: ConversationStatus }) =>
      apiClient(`/support-chat/conversations/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    onSuccess: invalidate,
  });
}

export function useMarkConversationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient(`/support-chat/conversations/${id}/read`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['support-chat', 'list'] });
      qc.invalidateQueries({ queryKey: ['support-chat', 'unread-count'] });
    },
  });
}

export function useStartConversation() {
  const invalidate = useInvalidateChat();
  return useMutation({
    mutationFn: (userId: number) =>
      apiClient<{ conversationId: number }>(`/support-chat/users/${userId}`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}
